import OverflowToolbarButton from "sap/m/OverflowToolbarButton";
import BaseController from "./BaseController";
import HorizontalLayout from "sap/ui/layout/HorizontalLayout";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import { EnvironmentService } from "../service/Environment.service";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import { Select$ChangeEvent } from "sap/m/Select";
import Item from "sap/ui/core/Item";
import Input, { Input$ValueHelpRequestEvent } from "sap/m/Input";
import { Link$PressEvent } from "sap/m/Link";
import { BesuchsberichtTeilnehmer } from "../model/models";
import Fragment from "sap/ui/core/Fragment";
import SelectDialog, { SelectDialog$ConfirmEvent, SelectDialog$SearchEvent } from "sap/m/SelectDialog";
import Context from "sap/ui/model/Context";
import Dialog from "sap/m/Dialog";
import Button, { Button$PressEvent } from "sap/m/Button";
import { FormValidator } from "../service/FormValidator.service";
import SimpleForm from "sap/ui/layout/form/SimpleForm";
import { ErrorHandler } from "../service/ErrorHandler.service";
import History from "sap/ui/core/routing/History";
import DatePicker, { DatePicker$ChangeEvent } from "sap/m/DatePicker";
import ComboBox from "sap/m/ComboBox";

/**
 * @namespace com.unitrade.crm.controller
 */
export default class VisitReport extends BaseController {
  private _pTeilnehmerSelectDialog: SelectDialog = null;
  private _pKundeSelectDialog: SelectDialog = null;
  private _pAddContactDetailsDialog: Dialog = null;
  private _oModel: JSONModel = null;
  private _metaModel: JSONModel = null;
  private _kundenModel: JSONModel = null;
  private _besuchsberichtArtModel: JSONModel = null;
  private _teilnehmerModel: JSONModel = null;
  private _selectedContact: JSONModel = null;

  private _backupInCaseOfCancel: any = null;

  private static BESUCHSBERICHT_GET_ENPOINT: string = "/besuchsbericht";
  private static BESUCHSBERICHT_SAVE_ENPOINT: string = "/besuchsbericht";
  private static KUNDEN_FIND_ENPOINT: string = "/kunde/find";
  private static BESUCHSBERICHT_ARTEN_ENPOINT: string = "/besuchsbericht/arten";
  private static TEILNEHMER_FIND_ENDPOINT: string = "/besuchsbericht/teilnehmer/find";
  private static TEILNEHMER_FIND_FOR_KUNDE_ENDPOINT: string = "/besuchsbericht/teilnehmer/findForKunde";

  async onInit(): Promise<void> {
    this._oModel = new JSONModel();
    this._metaModel = new JSONModel({
      busy: false,
      editButtonVisible: true,
      saveButtonVisible: true,
    });
    this._kundenModel = new JSONModel({
      selectedKundeKey: "",
      searchResult: [],
      busy: false,
    });
    this._besuchsberichtArtModel = new JSONModel([]);
    this._teilnehmerModel = new JSONModel({ searchResult: [] });
    this._selectedContact = new JSONModel({ id: 0 });
    this.setModel(this._oModel, "besuchsbericht");
    this.setModel(this._metaModel, "metamodel");
    this.setModel(this._kundenModel, "kunden");
    this.setModel(this._besuchsberichtArtModel, "besuchsberichtArten");
    this.setModel(this._teilnehmerModel, "teilnehmer");
    this.setModel(this._selectedContact, "selectedContact"); // the model for the ContactChangeDialog Fragment
    const oOwnerComponent = this.getOwnerComponent();
    const oRouter = oOwnerComponent.getRouter();

    oRouter.getRoute("visitReport").attachPatternMatched(this._onPatternMatch, this);
    oRouter.getRoute("visitReportForCustomer").attachPatternMatched(this._onPatternMatch, this);
  }

  onExit(): void {
    const oOwnerComponent = this.getOwnerComponent();
    const oRouter = oOwnerComponent.getRouter();
    oRouter.getRoute("visitReport").detachPatternMatched(this._onPatternMatch, this);
    oRouter.getRoute("visitReportForCustomer").detachPatternMatched(this._onPatternMatch, this);
  }

  private async _showFormFragment(sFragmentName: string): Promise<void> {
    const oBox = this.byId("visitreport_header_form") as HorizontalLayout;
    oBox.removeAllContent();
    const pFormFragment = await this.getFragment(sFragmentName);
    oBox.insertContent(pFormFragment, 0);
  }

  async handleSavePress() {
    const dataToSave = this._oModel.getData();
    const queryUrl = `${EnvironmentService.getBaseUrl()}${VisitReport.BESUCHSBERICHT_SAVE_ENPOINT}`;
    try {
      const formValid = this._validateBesuchsberichtForm();
      if (formValid) {
        this._metaModel.setProperty("/busy", true);
        const postReq: JQuery.UrlAjaxSettings = {
          contentType: "application/json; charset=utf-8",
          dataType: "json",
          url: queryUrl,
          data: JSON.stringify(dataToSave),
        };
        const model = await $.post(postReq);
        if (model.id != dataToSave.id) {
          this.getOwnerComponent().getRouter().navTo("visitReport", { idPath: model.id });
        }
        this._toggleButtonsAndView(false);
      }
    } catch (ex: any) {
      ErrorHandler.handleError(ex, { message: "Speichern war nicht erfolgreich", onRetry: this.handleSavePress.bind(this), ignorable: true });
    } finally {
      this._metaModel.setProperty("/busy", false);
    }
  }

  async handleCancelPress() {
    this._oModel.setData(this._backupInCaseOfCancel);
    this._backupInCaseOfCancel = null;

    this._toggleButtonsAndView(false);
  }

  async handleEditPress() {
    this._toggleButtonsAndView(true);
  }

  private _onPatternMatch = async (oEvent: Route$PatternMatchedEvent) => {
    this._backupInCaseOfCancel = null;
    this._oModel.setData({});
    this._kundenModel.setProperty("/selectedKundeKey", "");
    this._kundenModel.setProperty("/kundenSearchValue", "");

    const args = oEvent.getParameter("arguments") as {
      idPath: string;
      kundid: string;
      kundlfd: string;
    };
    const id = Number(args.idPath ?? 0);
    const kundid = Number(args.kundid ?? 0);
    const kundlfd = Number(args.kundlfd ?? 0);

    if (id > 0) {
      // edit mode
      this._toggleButtonsAndView(false);

      const queryUrl = `${EnvironmentService.getBaseUrl()}${VisitReport.BESUCHSBERICHT_GET_ENPOINT}/${id}`;
      try {
        this._metaModel.setProperty("/busy", true);
        await this._oModel.loadData(queryUrl);
        this.setReadonly(!this._oModel.getProperty("/canWrite"));
      } catch (ex: any) {
        MessageBox.error(`Besuchsbericht mit id ${id} konnte nicht geladen werden.`);
      } finally {
        this._metaModel.setProperty("/busy", false);
      }
    } else {
      // insert mode
      this.setReadonly(false);
      const username = localStorage.getItem("auth_username");
      const user = await $.get(`${EnvironmentService.getBaseUrl()}/user/username`, { username: username });
      this._oModel.setProperty("/id", 0);
      this._oModel.setProperty("/besuchAm", new Date().toISOString().substring(0, 19)); // i hate javascript
      this._oModel.setProperty("/istErledigt", false);
      this._oModel.setProperty("/teilnehmer", [{ name: `${user.name}`, key: "TE/" + username }]);
      this._toggleButtonsAndView(true);
    }

    if (kundid > 0 && kundlfd > 0) {
      this._oModel.setProperty("/kundenId", kundid);
      this._oModel.setProperty("/kundenLfd", kundlfd);
      try {
        const kunde = await $.get(`${EnvironmentService.getBaseUrl()}/kunde/${kundid}/${kundlfd}`);
        this._oModel.setProperty("/kundenName", kunde.kundenname);
        this._kundenModel.setProperty("/kundenSearchValue", kunde.kundenname);
      } catch (ex: any) {
        ErrorHandler.handleError(ex, { message: "Fehler beim Laden von Kundeninformationen" });
      }
    }
  };

  private async _toggleButtonsAndView(bEdit: boolean) {
    // Show the appropriate action buttons
    this._metaModel.setProperty("/editButtonVisible", !bEdit);
    (<OverflowToolbarButton>this.byId("visitreport_cancel_button")).setVisible(bEdit);

    if (bEdit) {
      const model = this._oModel.getData();
      if (model.id === 0) {
        // hide cancel button on insert mode!
        (<OverflowToolbarButton>this.byId("visitreport_cancel_button")).setVisible(false);
      }
      this._backupInCaseOfCancel = structuredClone(model);
      // Setze Kunden-Drowdown
      const kuId = this._oModel.getProperty("/kundenId");
      if (kuId) {
        const kuName = this._oModel.getProperty("/kundenName");
        this._kundenModel.setProperty("/kundenSearchValue", kuName);
      }

      // lade besuchsbericht arten
      await this._besuchsberichtArtModel.loadData(`${EnvironmentService.getBaseUrl()}${VisitReport.BESUCHSBERICHT_ARTEN_ENPOINT}`);
      const artId = this._oModel.getProperty("/artId");
      if (!artId) {
        const firstEntry = this._besuchsberichtArtModel.getData()[0];
        this._oModel.setProperty("/artId", firstEntry.id);
        this._oModel.setProperty("/artDescription", firstEntry.description);
      }
    }

    await this._showFormFragment(bEdit ? "VisitReportMetaChange" : "VisitReportMetaDisplay");
  }

  public onKundeConfirm = async (oEvent: SelectDialog$ConfirmEvent) => {
    const context = oEvent.getParameter("selectedItem").getBindingContext("kunden");
    if (context) {
      const id: number = context.getProperty("id");
      const lfd: number = context.getProperty("lfd");
      const name: string = context.getProperty("kundenname");

      this._oModel.setProperty("/kundenName", name);
      this._oModel.setProperty("/kundenId", id);
      this._oModel.setProperty("/kundenLfd", lfd);

      this.checkKundeValid();
    }
  };

  public onKundeSearch = async (oEvent: SelectDialog$SearchEvent) => {
    const queryUrl = `${EnvironmentService.getBaseUrl()}${VisitReport.KUNDEN_FIND_ENPOINT}`;
    const searchParam = oEvent.getParameter("value");
    const getReq: JQuery.UrlAjaxSettings = { url: queryUrl };
    if (searchParam) {
      getReq.data = { search: searchParam };
    }
    try {
      this._kundenModel.setProperty("/busy", true);
      const findResult = await $.get(getReq);
      this._kundenModel.setProperty("/searchResult", findResult);
    } catch (ex: any) {
      ErrorHandler.handleError(ex, { message: "Fehler bei Kundensuche" });
    } finally {
      this._kundenModel.setProperty("/busy", false);
    }
  };

  public async onTeilnehmerValueHelpRequest(oEvent: Input$ValueHelpRequestEvent) {
    const view = this.getView();
    await this.setTeilnehmerSearchResult();

    if (!this._pTeilnehmerSelectDialog) {
      this._pTeilnehmerSelectDialog = (await Fragment.load({
        id: view.getId(),
        name: "com.unitrade.crm.fragments.ContactSelectDialog",
        controller: this,
      })) as SelectDialog;

      const dialog = this._pTeilnehmerSelectDialog.getAggregation("_dialog") as Dialog;
      const beginButton = dialog.getAggregation("beginButton") as Button;
      const endButton = dialog.getAggregation("endButton") as Button;
      dialog.addButton(
        new Button({
          text: "Kontakt anlegen",
          enabled: "{= ${besuchsbericht>/kundenId} > 0}",
          press: () => {
            this.openAddContactDialog();
          },
        })
      );
      dialog.addButton(beginButton);
      dialog.addButton(endButton);

      view.addDependent(this._pTeilnehmerSelectDialog);
    }
    this._pTeilnehmerSelectDialog.open("");
  }

  public async onKundeValueHelpRequest(oEvent: Input$ValueHelpRequestEvent) {
    const kundenName: string = this._oModel.getProperty("/kundenName");
    this.showKundeSelectDialog(kundenName);
  }

  private async showKundeSelectDialog(search: string = "") {
    const view = this.getView();
    this._kundenModel.setProperty("/searchResult", []);
    if (!this._pKundeSelectDialog) {
      this._pKundeSelectDialog = (await Fragment.load({
        id: view.getId(),
        name: "com.unitrade.crm.fragments.CustomerSelectDialog",
        controller: this,
      })) as SelectDialog;

      view.addDependent(this._pKundeSelectDialog);
    }

    this._pKundeSelectDialog.open(search);
    this._pKundeSelectDialog.fireSearch({ value: search });
  }

  public async onTeilnehmerSearch(oEvent: SelectDialog$SearchEvent) {
    const search = oEvent.getParameter("value");
    await this.setTeilnehmerSearchResult(search);
  }

  public async onTeilnehmerSelected(oEvent: SelectDialog$ConfirmEvent) {
    const aContexts = oEvent.getParameter("selectedContexts") as Context[];
    const selectedTeilnehmer = aContexts.map((o) => <BesuchsberichtTeilnehmer>o.getObject());

    this._oModel.setProperty("/teilnehmer", selectedTeilnehmer);
  }

  private async setTeilnehmerSearchResult(search?: string): Promise<void> {
    const kuid = this._oModel.getProperty("/kundenId");
    const kulfd = this._oModel.getProperty("/kundenLfd");

    let findResult = await this.searchForTeilnehmer(search, kuid, kulfd);
    let teilnehmer = this._oModel.getProperty("/teilnehmer") as BesuchsberichtTeilnehmer[];
    teilnehmer = teilnehmer.map((t) => Object.assign(t, { selected: true }));

    findResult = findResult.filter((f) => teilnehmer.findIndex((t) => t.key == f.key) === -1);

    let resultToShow = teilnehmer.concat(findResult);
    resultToShow = resultToShow.map((t) =>
      Object.assign(t, {
        icon: t.key.startsWith("TE") ? "sap-icon://employee" : "sap-icon://address-book",
        description: t.key.startsWith("TE") ? t.key.split("/")[1] : null,
      })
    );

    this._teilnehmerModel.setProperty("/searchResult", resultToShow);
  }

  private async searchForTeilnehmer(search: string, kuid?: number, kulfd?: number): Promise<BesuchsberichtTeilnehmer[]> {
    const queryUrl =
      kuid && kulfd
        ? `${EnvironmentService.getBaseUrl()}${VisitReport.TEILNEHMER_FIND_FOR_KUNDE_ENDPOINT}/${kuid}/${kulfd}`
        : `${EnvironmentService.getBaseUrl()}${VisitReport.TEILNEHMER_FIND_ENDPOINT}`;
    const getReq: JQuery.UrlAjaxSettings = { url: queryUrl };
    if (search) {
      getReq.data = { search: search };
    }
    return await $.get(getReq);
  }

  /**
   * Method is called from the ContactChangeDialog Fragment
   * @param oEvent
   */
  dialogSavePress = async (oEvent: Button$PressEvent) => {
    const validationError = this._validateForm();

    if (!validationError) {
      const contact = this._selectedContact.getData();
      try {
        this._metaModel.setProperty("/busy", true);
        await this._createContact(contact);
        await this._pTeilnehmerSelectDialog.fireSearch();
        this._pAddContactDetailsDialog.close();
        this._selectedContact.setData({ id: 0 });
      } catch (oException: any) {
        MessageBox.error("Fehler beim Speichern von Kontakt: " + oException.statusText);
      } finally {
        this._metaModel.setProperty("/busy", false);
      }
    }
  };
  /**
   * Method is called from the ContactChangeDialog Fragment
   * @param oEvent
   */
  dialogCancelPress = (oEvent: Button$PressEvent) => {
    if (this._pAddContactDetailsDialog) {
      this._pAddContactDetailsDialog.close();
      this._selectedContact.setData({ id: 0 });
    }
  };

  private _validateBesuchsberichtForm(): boolean {
    const kundeValid = this.checkKundeValid();
    const besuchAmValid = this.checkBesuchAmValid();
    const betreffValid = this.checkBetreffValid();
    return kundeValid && besuchAmValid && betreffValid;
  }

  private checkKundeValid() {
    let bValid = true;
    const kundeField = this.byId("kunde") as Input;
    const kuid = this._oModel.getProperty("/kundenId");
    const kulfd = this._oModel.getProperty("/kundenLfd");
    if (!kuid || !kulfd) {
      if (kundeField) {
        kundeField.setValueState("Error");
        kundeField.setValueStateText("Ungültiger Kunde");
      }

      bValid = false;
    } else {
      if (kundeField) {
        kundeField.setValueState("None");
        kundeField.setValueStateText("");
      }
    }

    return bValid;
  }

  private checkBesuchAmValid() {
    let bValid = true;
    const besuchAmField = this.byId("besuch-am") as DatePicker;
    const besuchAm = this._oModel.getProperty("/besuchAm");
    if (!besuchAm) {
      if (besuchAmField) {
        besuchAmField.setValueState("Error");
        besuchAmField.setValueStateText("Ungültiges Datum");
      }

      bValid = false;
    } else {
      if (besuchAmField) {
        besuchAmField.setValueState("None");
        besuchAmField.setValueStateText("");
      }
    }

    return bValid;
  }

  private checkBetreffValid() {
    let bValid = true;
    const betreffField = this.byId("visit-report-view-combobox-betreff") as ComboBox;
    const betreff = this._oModel.getProperty("/betreff");
    if (!betreff) {
      betreffField.setValueState("Error");
      betreffField.setValueStateText("Bitte Zusammenfassung wählen");
      bValid = false;
    } else {
      betreffField.setValueState("None");
      betreffField.setValueStateText("");
    }

    return bValid;
  }

  public onBesuchAmChange(oEvent: DatePicker$ChangeEvent) {
    if (!oEvent.getParameter("valid")) {
      this._oModel.setProperty("/besuchAm", null);
    }
  }

  private _validateForm(): boolean {
    const oForm = this.byId("contact-change-dialog-fragment-form") as SimpleForm;
    return FormValidator.validate(oForm);
  }

  private _createContact = async (contact: any) => {
    const kuid = this._oModel.getProperty("/kundenId");
    const kulfd = this._oModel.getProperty("/kundenLfd");

    const url = `${EnvironmentService.getBaseUrl()}/kontakte/kunde/${kuid}/${kulfd}`;
    await $.post({
      url: url,
      data: JSON.stringify(contact),
    });
  };

  private async openAddContactDialog() {
    const kundenName = this._oModel.getProperty("/kundenName");
    this.setModel(new JSONModel({ kundenname: kundenName }), "Customer");
    if (!this._pAddContactDetailsDialog) {
      this._pAddContactDetailsDialog = (await this.loadFragment({ name: "com.unitrade.crm.fragments.ContactChangeDialog" })) as Dialog;
    }
    this._pAddContactDetailsDialog.open();
  }

  public onArtIdChange = (oEvent: Select$ChangeEvent) => {
    // Anpassen der ArtDescription
    const selectedItem = oEvent.getParameter("selectedItem");
    const description = selectedItem.getText();
    this._oModel.setProperty("/artDescription", description);
  };

  public onKundePress = (oEvent: Link$PressEvent) => {
    const kundid = this._oModel.getProperty("/kundenId");
    const kundlfd = this._oModel.getProperty("/kundenLfd");
    if (kundid && kundlfd) {
      this.getOwnerComponent().getRouter().navTo("customersDetails", { idPath: kundid, lfdPath: kundlfd });
    } else {
      this.showKundeSelectDialog();
    }
  };

  public teilnehmerToCommaSeparatedContacts(data: any[]) {
    if (data == null) {
      return "";
    }

    return data.map((z) => z.name).join(", ");
  }
  public besuchsberichtKundeHeaderFormatter(kundenname: string) {
    return kundenname ?? ">> KUNDE WÄHLEN";
  }

  public async getTitle(): Promise<string> {
    return "Besuchsbericht";
  }

  override navBackFunc: () => void = () => {
    const hash = History.getInstance().getPreviousHash();

    if (!hash || hash.includes("login")) {
      this.getRouter().navTo("home", {}, undefined, true);
    } else if (hash.includes("forCustomer")) {
      // previous page was the "create" page
      const parts = hash.split("/");
      this.getRouter().navTo("customersDetails", { idPath: parts[2], lfdPath: parts[3] }, undefined, true);
    } else {
      window.history.go(-1);
    }
  };

  private setReadonly(readonly: boolean) {
    this._metaModel.setProperty("/editButtonVisible", !readonly);
    this._metaModel.setProperty("/saveButtonVisible", !readonly);
    this._metaModel.setProperty("/berichtEditable", !readonly);
    this._metaModel.setProperty("/betreffEditable", !readonly);
  }
}
